import React, { useState, useMemo } from 'react';

import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { Permissions } from 'src/app_deprecated/constants/PermissionsConstants';

import { Button } from 'src/app/components/lib/button';
import { ControlBar, ControlGroup, ControlSearchBox, RefreshButton } from 'src/app/components/lib/control-bar';
import { PageLoader } from 'src/app/components/lib/loader';
import { getLinkTextCellColumn, DataGrid, generateRowActions } from 'src/app/components/lib/table';
import { useDeleteMarketingTagMutation } from 'src/app/queries/marketing/marketing-tags/delete-marketing-tag';
import { useGetAssociatedTagCampaignsMutation } from 'src/app/queries/marketing/marketing-tags/get-associated-tag-campaigns';
import { useGetMarketingTagsQuery } from 'src/app/queries/marketing/marketing-tags/get-marketing-tags';
import { useUpsertMarketingTagMutation } from 'src/app/queries/marketing/marketing-tags/upsert-marketing-tag';
import { Modal, useModal } from 'src/app/state/modals';
import { useNotificationActions } from 'src/app/state/notifications';
import { userPermissionsAtom } from 'src/app/state/user-permissions';

import { MarketingTagDetails } from './details';

import type { GridColDef } from 'src/app/components/lib/table';
import type { Tag } from 'src/app/queries/marketing/get-campaigns';
import type { AssociatedTagCampaign } from 'src/app/queries/marketing/marketing-tags/get-associated-tag-campaigns';
import type { MarketingTag } from 'src/app/queries/marketing/marketing-tags/get-marketing-tags';

export function MarketingTags() {
  const notifications = useNotificationActions();
  const navigate = useNavigate();

  const params = useParams()?.['*'];
  const isNewTag = useMemo(() => {
    const isNew = params?.includes('new');
    return isNew;
  }, [params]);
  const confirmModal = useModal(Modal.confirm);

  const [filter, setFilter] = useState<string>('');
  const [tagName, setTagName] = useState<string>('');
  const [isNew, setIsNew] = useState<boolean>(false);
  const [selected, setSelected] = useState<MarketingTag | null>(null);

  const { data: tags = [], isLoading, refetch } = useGetMarketingTagsQuery();
  const getAssociatedCampaignsMutation = useGetAssociatedTagCampaignsMutation();

  const upsertTagMutation = useUpsertMarketingTagMutation();
  const deleteTagMutation = useDeleteMarketingTagMutation();

  const { perms } = useRecoilValue(userPermissionsAtom);

  const filteredTags = useMemo(
    () =>
      tags.filter((tag) => {
        if (filter.trim().length > 0) {
          return tag.TagName.toLowerCase().includes(filter.trim().toLowerCase());
        }
        return true;
      }),
    [filter, tags]
  );

  const handleRefresh = () => {
    void refetch();
  };

  const handleAddTag = () => {
    if (isNewTag) {
      if (
        tags.find(
          (tag) =>
            tag.MarketingTagId !== selected?.MarketingTagId && tag.TagName.toLowerCase() === tagName.toLowerCase()
        )
      ) {
        notifications.error({ message: 'A tag with this name already exists' });
        return;
      }
    }

    if (tagName?.trim()) {
      upsertTagMutation.mutate(
        {
          MarketingTagId: isNewTag ? 0 : selected?.MarketingTagId || 0,
          TagName: tagName.trim(),
        },
        {
          onSuccess: () => {
            notifications.success({ message: 'Tag created successfully' });
          },
          onError: () => {
            notifications.error({ message: 'Failed to create tag' });
          },
        }
      );
    }
  };

  const handleDeleteTag = async (tagId: number) => {
    deleteTagMutation.mutate(
      {
        MarketingTagId: tagId,
      },
      {
        onSuccess: () => {
          notifications.success({ message: 'Tag deleted successfully' });
        },
        onError: () => {
          notifications.error({ message: 'Failed to delete tag' });
        },
      }
    );
  };

  const handleTagNameChange = (newTagName: string) => {
    setTagName(newTagName);
  };

  const navigateBack = () => {
    setIsNew(false);
    setSelected(null);
    setTagName('');
    navigate('.');
  };

  const onUpdateTag = () => {
    handleAddTag();

    setIsNew(false);
    setSelected(null);
    setTagName('');
    navigate('.');
  };

  const columns: GridColDef[] = [
    getLinkTextCellColumn({
      field: 'TagName',
      headerName: 'Tag Name',
    }),
    generateRowActions([
      {
        visible: !!perms[Permissions.CreateEditCampaigns],
        value: 'Delete',
        onClick: async (row) => {
          let hasAssociatedCampaigns: AssociatedTagCampaign[] = [];

          await getAssociatedCampaignsMutation.mutateAsync(row.MarketingTagId, {
            onSuccess: (data) => {
              hasAssociatedCampaigns = data;
            },
            onError: () => {
              notifications.error({ message: 'Failed to get associated campaigns' });
            },
          });

          confirmModal.openModal({
            helpText:
              hasAssociatedCampaigns.length > 0
                ? `This tag is associated with ${hasAssociatedCampaigns.length} campaign(s). Deleting this tag will remove it from all associations. This action cannot be undone.`
                : 'Are you sure you want to delete this tag? This action cannot be undone.',
            onConfirm: async () => {
              try {
                await handleDeleteTag(row.MarketingTagId);
              } catch (e) {
                console.error(e);
              }
            },
          });
        },
      },
    ]),
  ];

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <Routes>
      <Route
        element={
          <>
            <ControlBar>
              <ControlGroup grow>
                <ControlSearchBox initialValue={filter} placeholder='Search marketing tags...' onChange={setFilter} />
                <RefreshButton onClick={handleRefresh} />
                {!!perms[Permissions.CreateEditCampaigns] && (
                  <Button
                    data-testid='marketing-add-tag-button'
                    disabled={upsertTagMutation.isLoading}
                    label='Add Tag'
                    onClick={() => {
                      navigate('./new');
                      setIsNew(true);
                    }}
                  />
                )}
              </ControlGroup>
            </ControlBar>
            <DataGrid
              columns={columns}
              getRowId={(row) => row.MarketingTagId}
              height='100%'
              name='marketing-tags'
              rows={filteredTags}
              onRowClick={({ row }) => {
                setSelected(row as MarketingTag);
                setIsNew(false);
                navigate(`./${String(row.MarketingTagId)}`);
              }}
            />
          </>
        }
        path='/'
      />

      <Route
        element={
          <MarketingTagDetails
            isNew={isNew}
            isValid={() => tagName.trim().length > 0}
            navigateBack={navigateBack}
            selected={selected as Tag}
            tagName={tagName}
            onTagNameChange={handleTagNameChange}
            onUpdateTag={onUpdateTag}
          />
        }
        path=':TagId'
      />
    </Routes>
  );
}
